import type { Json, Tables } from "@/integrations/supabase/types";


// Plaintext columns were dropped after the encryption rollout. The fields below are
// hydrated at read-time from `_preview` aliases and `get_decrypted_*` RPCs, so we
// extend the generated row types with virtual plaintext fields for app-level code.
export type ContentVoiceProfile = Tables<"content_voice_profiles"> & {
  // Opt-in second voice mode (column may not yet exist in regenerated types).
  dual_voice_modes_enabled: boolean;
  // Free-form text fields (hydrated from *_preview columns +
  // get_decrypted_voice_profiles RPC).
  avoid_list: string | null;
  phrase_preferences: string | null;
  // Aethyrius-parity extensions (all optional plaintext, hydrated from
  // *_preview columns and the get_decrypted_voice_profiles RPC).
  core_identity: string | null;
  voice_mode_a_notes: string | null;
  voice_mode_b_notes: string | null;
  sentence_architecture: string | null;
  paragraph_flow: string | null;
  vocabulary_fingerprint: string | null;
  signature_phrases: string | null;
  verbal_markers: string | null;
  rhetorical_moves: string | null;
  primary_tone: string | null;
  secondary_tone: string | null;
  formality_position: string | null;
  emotional_register: string | null;
  tradition_synthesis: string | null;
  red_lines: string | null;
  common_misrepresentations: string | null;
  peak_author_examples: string | null;
  authenticity_markers: string | null;
  lexical_compliance: string | null;
  building_block_protocols: string | null;
};
export type ContentAvatar = Tables<"content_avatars"> & {
  summary: string | null;
  notes: string | null;
};
export type ContentFramework = Tables<"content_frameworks">;
export type ContentFrameworkColumn = Tables<"content_framework_columns">;
export type ContentFrameworkPrinciple = Tables<"content_framework_principles">;
export type ContentFrameworkTopic = Tables<"content_framework_topics">;
export type ContentChip = Tables<"content_chips">;
export type ContentSource = Tables<"content_sources">;
// Wave 3 (Option B) note: in code we still call this `ContentBrief` for
// back-compat, but in the locked vocabulary it is the **Piece** container
// (DB table: content_pieces). The new planning Brief lives in `content_briefs`
// (Planning-brief CRUD helpers were removed 2026-06-13; see content_briefs table directly.)
export type ContentBrief = Tables<"content_pieces"> & {
  notes: string | null;
  prompt: string | null;
};
export type ContentBriefReference = Tables<"content_piece_references">;
export type ContentBriefPlan = Tables<"content_brief_plans">;
export type ContentBriefDraft = {
  id: string;
  workspace_id: string;
  piece_id: string;
  plan_id: string | null;
  requested_by_user_id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  model: string | null;
  draft_title: string | null;
  draft_body: string | null;
  draft_summary: string | null;
  revision_notes: string | null;
  error_message: string | null;
  context_snapshot: Json;
  piece_updated_at_snapshot: string | null;
  plan_updated_at_snapshot: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // Wave B: revision system. AI rows default to 'ai' with draft_doc = null.
  revision_kind?: "ai" | "user_edit";
  draft_doc?: unknown | null;
  parent_draft_id?: string | null;
  last_edited_by_user_id?: string | null;
};

export type ContentDestinationFormat =
  | "markdown"
  | "html"
  | "plaintext"
  | "newsletter"
  | "social_short"
  | "social_thread"
  | "book_chapter";

export type ContentBriefPublication = {
  id: string;
  workspace_id: string;
  piece_id: string;
  draft_id: string;
  requested_by_user_id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  destination_format: ContentDestinationFormat;
  /** Optional Publish Type key (e.g. "article", "book_chapter"). See src/lib/publishTypes.ts. */
  publish_type_key: string | null;
  model: string | null;
  prepared_title: string | null;
  prepared_body: string | null;
  prepared_excerpt: string | null;
  prepared_metadata: Json;
  revision_notes: string | null;
  error_message: string | null;
  draft_updated_at_snapshot: string | null;
  piece_updated_at_snapshot: string | null;
  context_snapshot: Json;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ContentReferenceType = Extract<
  ContentBriefReference["reference_type"],
  "voice_profile" | "avatar" | "column" | "principle" | "topic" | "source" | "brief" | "readwise_highlight" | "excerpt"
>;

export type ReadwiseHighlightSummary = {
  id: string;
  workspace_id: string;
  highlight_text: string;
  book_title: string | null;
  book_author: string | null;
  note: string | null;
};

export type ExcerptReferenceSummary = {
  id: string;
  workspace_id: string;
  body_text: string;
  notes: string | null;
  author_name: string | null;
  publication_title: string | null;
  library_title: string | null;
};

export type ContentChipColorKey = "silver" | "indigo" | "sage" | "amber" | "coral" | "sky" | "plum";
export type ContentCapability =
  | "composer_access"
  | "content_frameworks"
  | "content_avatars"
  | "content_sources"
  | "content_briefs"
  | "content_reference_mentions"
  | "content_planning_agent"
  | "content_writing_agent"
  | "content_publishing_prep"
  | "content_publishing_dispatch"
  | "content_multi_workspace";

export type ContentConnectorType =
  | "webhook"
  | "email"
  | "kit"
  | "bento"
  | "wordpress"
  | "wordpress_self_hosted"
  | "ghost"
  | "substack_unofficial"
  | "beehiiv"
  | "buttondown";
export type ContentConnectorStatus = "active" | "disabled" | "error";

export const EMAIL_RECIPIENT_HARD_CAP = 25;
export const EMAIL_RECIPIENT_MONTHLY_SOFT_CAP = 5000;

export type ContentPublishingConnector = {
  id: string;
  workspace_id: string;
  connector_type: ContentConnectorType;
  display_name: string;
  status: ContentConnectorStatus;
  config: Json;
  created_by_user_id: string;
  last_used_at: string | null;
  last_error_message: string | null;
  created_at: string;
  updated_at: string;
};

export type ContentDispatchStatus = "queued" | "running" | "succeeded" | "failed";

export type ContentPublishingDispatch = {
  id: string;
  workspace_id: string;
  publication_id: string | null;
  connector_id: string | null;
  status: ContentDispatchStatus;
  requested_by_user_id: string;
  request_snapshot: Json;
  response_snapshot: Json;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ComposerSnapshot = {
  voiceProfile: ContentVoiceProfile | null;
  avatars: ContentAvatar[];
  frameworks: ContentFramework[];
  columns: ContentFrameworkColumn[];
  principles: ContentFrameworkPrinciple[];
  topics: ContentFrameworkTopic[];
  chips: ContentChip[];
  sources: ContentSource[];
  briefs: ContentBrief[];
  briefReferences: ContentBriefReference[];
  briefPlans: ContentBriefPlan[];
  briefDrafts: ContentBriefDraft[];
  briefPublications: ContentBriefPublication[];
  publishingConnectors: ContentPublishingConnector[];
  publishingDispatches: ContentPublishingDispatch[];
  readwiseHighlights: ReadwiseHighlightSummary[];
  excerpts: ExcerptReferenceSummary[];
};

export type BriefReferenceInput = {
  referenceType: ContentReferenceType;
  referenceId: string;
  labelSnapshot?: string | null;
  metadata?: Json;
  sortOrder?: number;
  /**
   * Optional initial cited flag. Defaults to the DB default (`true`) when omitted.
   * Pass `false` when adding via the "Add to bibliography" path so the entry
   * lands as background-reading-only (no inline footnote/endnote number).
   */
  cited?: boolean;
};

export type ContentReferenceOption = {
  id: string;
  referenceType: ContentReferenceType;
  label: string;
  description: string | null;
  groupKey: ContentReferenceType;
  groupLabel: string;
  colorKey: string | null;
  token: string;
};

export type ContentReferenceGroup = {
  key: ContentReferenceType;
  label: string;
  options: ContentReferenceOption[];
};

export type ResolvedBriefReference = ContentBriefReference & {
  label: string;
  groupLabel: string;
  description: string | null;
  colorKey: string | null;
};

export type ContentBriefWithReferences = ContentBrief & {
  references: ResolvedBriefReference[];
};

export type BriefPlanRecommendedReference = {
  referenceType: ContentReferenceType;
  label: string;
  reason: string;
};

export type BriefPlanStructureItem = {
  section: string;
  angle: string;
  goal: string;
};

export type ContentBriefPlanParsed = ContentBriefPlan & {
  recommendedReferencesParsed: BriefPlanRecommendedReference[];
  suggestedStructureParsed: BriefPlanStructureItem[];
};

export type RunBriefPlanningResult = {
  plan: ContentBriefPlanParsed;
};

export type ContentBriefDraftParsed = ContentBriefDraft & {
  contextSnapshotParsed: Record<string, Json | null>;
};

export type RunBriefDraftInput = {
  briefId: string;
  revisionPrompt?: string;
  planId?: string | null;
};

export type RunBriefDraftResult = {
  draft: ContentBriefDraftParsed;
};

export type ContentBriefPublicationParsed = ContentBriefPublication & {
  preparedMetadataParsed: Record<string, Json | null>;
};

export type RunBriefPublicationInput = {
  briefId: string;
  draftId: string;
  destinationFormat: ContentDestinationFormat;
  revisionPrompt?: string;
  /** Optional Publish Type key (e.g. "article", "book_chapter") used by the Publisher UI. */
  publishTypeKey?: string | null;
};

export type RunBriefPublicationResult = {
  publication: ContentBriefPublicationParsed;
};
